import {ref} from 'vue';
import type {TypedAggregationsRecord} from '@common/composables';
import {useFiltersActions, useSearchFilter} from '@common/composables';
import {useScreenSizeStore} from '@common/stores';
import type {AvailableSearchFilters} from '@features/projects-list/types';
import {availableFiltersTypes} from '@features/projects-list/types';
import {filterDisplayableManagerBuckets} from '@features/projects-list/utils';
import {projectListEndpoints} from '@registry';
import cloneDeep from 'lodash-es/cloneDeep';
import {defineStore, storeToRefs} from 'pinia';
import {useLogger, useAbortableApi} from '#imports';
import {withCleanup} from '@malt/utils';
import {useProjectsListStore} from './projects-list.store';

/**
 * Store for handling project list page search, list and filter
 */
export const useProjectsListFiltersStore = defineStore('projects-list-filters', () => {
    const logger = useLogger();

    const screeSizeStore = useScreenSizeStore();
    const {isSmallScreen} = storeToRefs(screeSizeStore);

    const projectListStore = useProjectsListStore();
    const {
        searchParams,
        formattedResults,
        freelancerAggregations,
        ownerAggregations,
        companyAggregations,
        organizationAggregations,
        statusAggregations,
        actionsAggregations,
        companyManagerAggregations,
        finOpsManagerAggregations,
        companySuccessManagerAggregations,
        contractTypeAggregations,
        verticalTypeAggregations,
        opportunityTypeAggregations,
        renewalStatusAggregations,
        searchResult,
    } = storeToRefs(projectListStore);

    // - Filters

    const temporarySearchParams = ref<AvailableSearchFilters>(cloneDeep(searchParams.value));

    function triggerSearchOrGetNumberOfResults() {
        if (isSmallScreen.value) {
            getNumberOfResultsForTemporaryFilters();
        } else {
            searchParams.value.page = 0;
        }
    }

    const {handleResetAllCategories, handleResetCategory, handleUpdateCategory, selectedFiltersCount, isAnyFilterSelected} = useFiltersActions({
        filters: searchParams,
        tempFilters: temporarySearchParams,
        availableFilters: availableFiltersTypes,
        onUpdate: triggerSearchOrGetNumberOfResults,
    });

    const {execute: filterAggregateRequest, abort: abortFilterAggregate} = useAbortableApi(projectListEndpoints, 'filterAggregate');

    // - Search  filters
    async function fetchSearchedAggregations(
        category:
            | 'freelancers'
            | 'owners'
            | 'companies'
            | 'organizations'
            | 'companyManagers'
            | 'finOpsManagers'
            | 'customerSuccessManagers'
            | 'contractTypes'
            | 'verticalTypes'
            | 'opportunityTypes',
        aggregateFilter: string,
    ) {
        try {
            abortFilterAggregate();
            const data: TypedAggregationsRecord<AvailableSearchFilters> = await filterAggregateRequest({
                ...searchParams.value,
                aggregateName: category,
                aggregateFilter,
            });
            return data;
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    const ownerFilterProperties = useSearchFilter({
        aggregations: ownerAggregations,
        async onSearch(value) {
            try {
                const {owners} = await fetchSearchedAggregations('owners', value);
                return owners;
            } catch {}
        },
    });

    const companyFilterProperties = useSearchFilter({
        aggregations: companyAggregations,
        async onSearch(value) {
            try {
                const {companies} = await fetchSearchedAggregations('companies', value);
                return companies;
            } catch {}
        },
    });

    const organizationFilterProperties = useSearchFilter({
        aggregations: organizationAggregations,
        async onSearch(value) {
            try {
                const {organizations} = await fetchSearchedAggregations('organizations', value);
                return organizations;
            } catch {}
        },
    });

    const freelancerFilterProperties = useSearchFilter({
        aggregations: freelancerAggregations,
        async onSearch(value) {
            try {
                const {freelancers} = await fetchSearchedAggregations('freelancers', value);
                return freelancers;
            } catch {}
        },
    });

    const companyManagerFilterProperties = useSearchFilter({
        aggregations: companyManagerAggregations,
        async onSearch(value) {
            try {
                const {companyManagers} = await fetchSearchedAggregations('companyManagers', value);
                return companyManagers;
            } catch {}
        },
    });

    const finOpsManagerFilterProperties = useSearchFilter({
        aggregations: finOpsManagerAggregations,
        async onSearch(value) {
            try {
                const {finOpsManagers} = await fetchSearchedAggregations('finOpsManagers', value);
                return filterDisplayableManagerBuckets(finOpsManagers);
            } catch {}
        },
    });

    const customerSuccessManagerFilterProperties = useSearchFilter({
        aggregations: companySuccessManagerAggregations,
        async onSearch(value) {
            try {
                const {customerSuccessManagers} = await fetchSearchedAggregations('customerSuccessManagers', value);
                return filterDisplayableManagerBuckets(customerSuccessManagers);
            } catch {}
        },
    });

    const contractTypeFilterProperties = useSearchFilter({
        aggregations: contractTypeAggregations,
        async onSearch(value) {
            try {
                const {contractTypes} = await fetchSearchedAggregations('contractTypes', value);
                return contractTypes;
            } catch {}
        },
    });

    const verticalTypeFilterProperties = useSearchFilter({
        aggregations: verticalTypeAggregations,
        async onSearch(value) {
            try {
                const {verticalTypes} = await fetchSearchedAggregations('verticalTypes', value);
                return verticalTypes;
            } catch {}
        },
    });

    const opportunityTypeFilterProperties = useSearchFilter({
        aggregations: opportunityTypeAggregations,
        async onSearch(value) {
            try {
                const {opportunityTypes} = await fetchSearchedAggregations('opportunityTypes', value);
                return opportunityTypes;
            } catch {}
        },
    });

    // - Temporary filters for mobile/tablet

    const temporaryNumberOfResults = ref<number>(searchResult.value?.totalElements ?? 0);
    const loadingTemporaryResults = ref(false);

    function initTemporarySearchParams() {
        temporarySearchParams.value = cloneDeep(searchParams.value);
        temporaryNumberOfResults.value = formattedResults.value.length;
    }

    function saveTemporaryParamsAndSearch() {
        searchParams.value = {...searchParams.value, ...temporarySearchParams.value};
    }

    async function getNumberOfResultsForTemporaryFilters() {
        try {
            loadingTemporaryResults.value = true;
            const result = await projectListStore.getFilteredProjectListLength({
                ...searchParams.value,
                ...temporarySearchParams.value,
                page: 0,
            });
            temporaryNumberOfResults.value = result;
        } catch (e) {
            logger.error(e);
        } finally {
            if (!projectListStore.searchProjectListAborted) {
                loadingTemporaryResults.value = false;
            }
        }
    }

    return withCleanup({
        actionsAggregations,
        freelancerAggregations,
        ownerAggregations,
        statusAggregations,
        contractTypeAggregations,
        verticalTypeAggregations,
        opportunityTypeAggregations,
        renewalStatusAggregations,
        handleResetCategory,
        handleUpdateCategory,
        handleResetAllCategories,
        isAnyFilterSelected,
        selectedFiltersCount,
        temporarySearchParams,
        initTemporarySearchParams,
        temporaryNumberOfResults,
        loadingTemporaryResults,
        saveTemporaryParamsAndSearch,
        freelancerFilterProperties,
        ownerFilterProperties,
        companyFilterProperties,
        organizationFilterProperties,
        companyManagerFilterProperties,
        finOpsManagerFilterProperties,
        customerSuccessManagerFilterProperties,
        contractTypeFilterProperties,
        verticalTypeFilterProperties,
        opportunityTypeFilterProperties,
    });
});
